import { CircularProgress } from '@material-ui/core';
import type { ProjectFileData } from '@mogul/api-client';
import { NftInclude, ProjectInclude } from '@mogul/api-client';
import { constants } from 'ethers';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import * as r from 'runtypes';

import { projectsApi } from '../../../api';
import { getNftIfMinted } from '../../../api-helpers/nft';
import { createBuyStarsModal } from '../../../components/buy-stars-modal';
import { BackHeader } from '../../../components/common/backheader/BackHeader.component';
import { Button } from '../../../components/core/button';
import type { CastAndCrewData } from '../../../components/movie/card/AdditionalInfo.component';
import { createWithdrawStarsModal } from '../../../components/movie/modal/WithdrawStarsModal.component';
import { Project } from '../../../components/movie/Project.component';
import { defaultYoutubeLink } from '../../../constants/links';
import { ModalStateContext } from '../../../contexts/app-state-context/modal-state.context';
import { useNetwork, useSwitchChain, useWeb3State } from '../../../hooks';
import { useMovieVotingContract } from '../../../hooks/movie-voting.hooks';
import { useNFTContract } from '../../../hooks/nft-contract.hooks';
import { useStarsContract } from '../../../hooks/stars-contract.hooks';
import type { MovieRound } from '../../../hooks/voting.hooks';
import {
  useVotingContract,
  VotingRoundState,
} from '../../../hooks/voting.hooks';
import { CHAIN_ID_TO_TYPE_MAP, WEB3_CONFIG } from '../../../lib';
import styles from './ProjectPage.module.css';

const INITIAL_VOTING_ROUND: MovieRound = {
  roundId: '',
  movieIds: [],
  votes: [],
  startBlock: '0',
  endBlock: '0',
  starsRewards: '0',
  votingState: VotingRoundState.Canceled,
};

const INITIAL_MOVIE_META: ProjectMetadata = {
  description: '',
  genre: '',
  image: '',
  name: '',
  studio: '',
};

const ZERO_BLOCK = {
  timestamp: 0,
  number: 0,
};

export interface ProjectMetadata {
  description: string;
  genre: string;
  image: string;
  name: string;
  studio: string;
}

export const ProjectPage: React.FC = () => {
  const history = useHistory();
  const { walletDetails } = useWeb3State();
  const { switchNetworkAsync } = useSwitchChain();
  const { isUnsupported } = useNetwork();

  const starsContract = useStarsContract();

  const movieVotingContract = useMovieVotingContract({
    overrideAddress: WEB3_CONFIG.contracts.eth.movieVoting,
    overrideProvider: CHAIN_ID_TO_TYPE_MAP[WEB3_CONFIG.chains.eth.id],
    hardOverride: false,
  });

  const votingContract = useVotingContract({
    overrideAddress: WEB3_CONFIG.contracts.bsc.movieVoting,
    overrideProvider: CHAIN_ID_TO_TYPE_MAP[WEB3_CONFIG.chains.bsc.id],
    hardOverride: false,
  });

  const {
    getVotingRoundInfo,
    getUserStarsForMovie,
    calculateStarsRewards,
    getCurrentBlock,
    getBlockTimestamp,
    vote,
    removeVotes,
  } =
    walletDetails.chain.id === WEB3_CONFIG.chains.eth.id
      ? movieVotingContract
      : votingContract;

  const nftContract = useNFTContract();

  const [votingRound, setVotingRound] = useState(INITIAL_VOTING_ROUND);
  const [totalStarsEntered, setTotalStarsEntered] = useState('0');
  const [totalRewards, setTotalRewards] = useState('0');
  const [movieMetadata, setMovieMetadata] = useState(INITIAL_MOVIE_META);
  const [trailerUrl, setTrailerUrl] = useState('');
  const [budget, setBudget] = useState('');
  const [cast, setCast] = useState<CastAndCrewData[]>([]);
  const [currentBlock, setCurrentBlock] = useState(ZERO_BLOCK);
  const [startBlock, setStartBlock] = useState(ZERO_BLOCK);
  const [endBlock, setEndBlock] = useState(ZERO_BLOCK);
  const [winningVotes, setWinningVotes] = useState('0');
  const [hasBeenFinanced, setHasBeenFinanced] = useState(false);
  const [loading, setLoading] = useState(true);
  const [additionalDetails, setAdditionalDetails] = useState<ProjectFileData[]>(
    [],
  );

  const { roundId, movieId } = r
    .Record({ roundId: r.String, movieId: r.String })
    .check(useParams());

  const setProjectData = async () => {
    const nft = await getNftIfMinted(
      WEB3_CONFIG.contracts[walletDetails.chain.key].movieVotingNft,
      movieId,
      [NftInclude.Project],
    );

    const movieData = nft?.project;

    if (!movieData) {
      return;
    }

    const movieFileAttachmentsResponse = await projectsApi.getProject({
      id: movieData.id,
      include: [ProjectInclude.AttachedFiles],
    });
    const movieFileAttachmentsData: ProjectFileData[] =
      movieFileAttachmentsResponse.data.data.attachedFiles ?? [];
    setAdditionalDetails(movieFileAttachmentsData);

    const result = {
      description: movieData.description,
      genre: movieData.genre ?? '',
      image: movieData.posterImage?.publicUrl ?? '',
      name: movieData.title,
      studio: movieData.studio ?? '',
    };

    const crewData = movieData.crew as CastAndCrewData[];
    const castData = movieData.cast as CastAndCrewData[];
    const castAndCrewData = crewData.concat(
      castData.map(castObj => ({ ...castObj, role: 'Actor' })),
    );

    const round = await getVotingRoundInfo(roundId);
    const totalStars = await getUserStarsForMovie(roundId, movieId);
    const rewards = await calculateStarsRewards(roundId);
    const block = await getCurrentBlock();
    const startBlockTimestamp = await getBlockTimestamp(
      parseInt(round.startBlock, 10),
    );
    const endBlockTimestamp = await getBlockTimestamp(
      parseInt(round.endBlock, 10),
    );
    const winningMovieVotes = round.votes.reduce((a, b) => {
      return parseInt(a, 10) > parseInt(b, 10) ? a : b;
    });

    setWinningVotes(winningMovieVotes);

    setHasBeenFinanced(movieData.isFinanced);

    setMovieMetadata(result);
    setVotingRound(round);
    setBudget(movieData.budget?.toLocaleString() ?? '0');
    setTrailerUrl(movieData.trailerUrl ?? defaultYoutubeLink);
    setCast(castAndCrewData);
    setTotalStarsEntered(totalStars);
    setTotalRewards(rewards);
    setCurrentBlock(block);
    setStartBlock({
      timestamp: startBlockTimestamp,
      number: parseInt(round.startBlock, 10),
    });
    setEndBlock({
      timestamp: endBlockTimestamp,
      number: parseInt(round.endBlock, 10),
    });
    setLoading(false);
  };

  useEffect(() => {
    if (!nftContract.initialized) {
      return;
    }

    setLoading(true);
    void setProjectData();
  }, [nftContract.initialized, walletDetails]);

  const { setModal } = useContext(ModalStateContext);

  const handleClickGetMoreStars = () => {
    setModal(createBuyStarsModal());
  };

  const handlePlaceVote = async (starsAmount: string) => {
    const allowance = await starsContract.allowance(
      walletDetails.address ?? '',
      WEB3_CONFIG.contracts[walletDetails.chain.key].movieVotingChef,
    );

    let result;
    if (parseFloat(allowance) < parseFloat(starsAmount)) {
      result = await starsContract.approve(
        WEB3_CONFIG.contracts[walletDetails.chain.key].movieVotingChef,
        constants.MaxUint256,
      );
    } else {
      result = await vote(roundId, movieId, starsAmount);
    }

    if (result) {
      void setProjectData();
    }

    return result;
  };

  const handleClickWithdrawStars = async (starsAmt: string) => {
    const result = await removeVotes(roundId, movieId, starsAmt);

    if (result) {
      void setProjectData();
    }
    return result;
  };

  const handleWithdrawStars = () => {
    setModal(
      createWithdrawStarsModal({
        totalStarsEntered,
        totalRewards,
        rewardsPerBlock:
          parseFloat(votingRound.starsRewards) /
          (parseInt(votingRound.endBlock, 10) -
            parseInt(votingRound.startBlock, 10)),
        withdrawStars: handleClickWithdrawStars,
      }),
    );
  };

  const handleBnbSwitch = useCallback(
    () => switchNetworkAsync?.(WEB3_CONFIG.chains.bsc.id),
    [switchNetworkAsync],
  );

  if (isUnsupported) {
    return (
      <div
        className={
          styles.moviePageContainer +
          ' flex flex-col items-center justify-center'
        }
      >
        <div className="flex flex-col items-center justify-center w-full h-full">
          <div className="flex flex-col items-center justify-center h-full max-w-screen-lg w-4/5">
            <p className="text-lg">You are using the wrong network ...</p>
            <Button className="mt-8" onClick={handleBnbSwitch}>
              Switch To BNB Chain
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (votingRound.movieIds.length === 0 && !loading) {
    return (
      <div className={styles.movieVotingPage}>
        <div className={styles.movieVotingContainer + ' max-w-screen-lg mt-10'}>
          <div className={'w-full flex justify-between'}>
            <div>
              <div
                className={
                  styles.voting +
                  ' 2xl:text-7xl xl:text-6xl lg:text-5xl text-4xl'
                }
              >
                VOTING
              </div>
            </div>
          </div>
          <div className="py-10">
            <div className={'w-full lg:w-full pb-3 text-base xl:text-lg'}>
              The Mogul voting contract uses a quadratic formula to calculate
              how many votes each $MOGUL holder gets when voting on which
              project Mogul will finance.
            </div>
            <div className={'w-full lg:w-full pb-3 text-base xl:text-lg'}>
              The number of votes a $MOGUL holder receives is equal to the
              square root of the number of $MOGUL they have.
            </div>
            <div className={'w-full lg:w-full text-base xl:text-lg'}>
              For more on how quadratic voting works, visit Mogul Production's{' '}
              <a
                className="underline"
                href="https://mogulproductions.gitbook.io/mogul-productions/product-information/voting"
              >
                Gitbook
              </a>
              .
            </div>
          </div>
          <div
            className={'w-full lg:w-full pb-3 text-base xl:text-lg'}
            style={{ fontWeight: 'bold' }}
          >
            There are currently no active voting rounds, please check back
            later.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        styles.moviePageContainer + ' flex flex-col items-center justify-center'
      }
    >
      {!loading && (
        <div className="w-full ml-16 mt-20 sm:mt-0">
          <BackHeader
            title="back to voting"
            headerOnPress={() => history.push('/platform/voting')}
          />
        </div>
      )}
      {loading ? (
        <CircularProgress
          className={'grid place-items-center w-full h-full text-white'}
          style={{ color: 'white' }}
          size={100}
        />
      ) : (
        <Project
          isWinner={
            winningVotes !== '0' &&
            winningVotes ===
              votingRound.votes[votingRound.movieIds.indexOf(movieId)]
          }
          hasBeenFinanced={hasBeenFinanced}
          votingState={votingRound.votingState}
          currentBlock={currentBlock}
          startBlock={startBlock}
          endBlock={endBlock}
          numberOfVotes={
            votingRound.votes[votingRound.movieIds.indexOf(movieId)]
          }
          starsSubmitted={totalStarsEntered}
          rewardGenerated={totalRewards}
          rewardsPerBlock={
            parseFloat(votingRound.starsRewards) /
            (parseInt(votingRound.endBlock, 10) -
              parseInt(votingRound.startBlock, 10))
          }
          additionalDetails={additionalDetails}
          getMoreStars={handleClickGetMoreStars}
          placeVote={handlePlaceVote}
          withdrawStars={handleWithdrawStars}
          trailerLink={trailerUrl}
          budget={budget}
          name={movieMetadata.name}
          studio={movieMetadata.studio}
          image={movieMetadata.image}
          description={movieMetadata.description}
          cast={cast}
        />
      )}
    </div>
  );
};
